import React, { useRef, useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
//components
import VerifyPassword from 'pages/verify-password';
import { AppShell } from '../app/AppShell';
import InviteExpire from 'wrapper/invite-expire';
import EmailVerified from 'wrapper/email-verified';
import VerifyEmail from 'wrapper/verify-email';
import PersonalInfoCard from 'wrapper/personal-info';
import SessionExpire from 'wrapper/session-expire/SessionExpire';
import Loader from 'wrapper/loader';
import B2CDataPreloader from 'components/B2CDataPreloader';
import B2BDataPreloader from 'components/B2BDataPreloader';
//constants
import { roles } from 'constants/constant.js';
import { useDispatch } from 'react-redux';
//redux
import { setUserRole } from 'redux_store/slices/userRole';
import { useGetPersonalDetailsQuery } from 'redux_store/services/client/profile';
//utils
import { hasPermissions } from 'constants/permissionChecker';
import {
  fetchCurrentConsumer,
  fetchCurrentUser,
} from 'services/stytchAuthService';
import { performLogout } from 'utils/logout';
import { cacheUserMetadata, CheckAuth } from 'auth/auth';
import * as Sentry from '@sentry/browser';

//this is the main component that validates login and renders dashboard (i.e : AppShell.jsx)

const Login = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();

  const [userData, setUserData] = useState(undefined);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoadingUser, setIsLoadingUser] = useState(true);
  //extract variables from search params
  const searchParams = new URLSearchParams(location?.search);
  const userId = searchParams.get('user_id');
  const code = searchParams.get('code');
  const email = searchParams.get('email');

  const [isOnboarding, setIsOnboarding] = useState(false);
  const [data, setData] = useState(undefined);

  const navigationDoneRef = useRef(false);

  //handle hover state for icon

  useEffect(() => {
    const loadUser = async () => {
      try {
        // Skip cached user when on a login page — the user explicitly wants
        // to (re)authenticate, possibly as a different account.
        const currentPath = window.location.pathname || '';
        const isLoginPage =
          currentPath.includes('/login') || currentPath.includes('/stytch-login');

        if (isLoginPage) {
          // On login pages, don't auto-authenticate — let the user
          // log in as a different account via the login form.
          setIsAuthenticated(false);
          return;
        }

        // Use cached user if available — App.jsx validates the session in background
        const cachedUser = CheckAuth();
        if (cachedUser) {
          setUserData(cachedUser);
          setData(cachedUser);
          setIsAuthenticated(true);
          return;
        }

        let resolvedUser = null;
        let resolvedAuthContext = null;

        // Only check B2C endpoint if explicitly in B2C flow (via URL param)
        const searchParamsAuth = new URLSearchParams(window.location.search);
        const isB2CFlow = searchParamsAuth.get('flow') === 'b2c';

        if (isB2CFlow) {
          try {
            const consumerResponse = await fetchCurrentConsumer();
            const consumerUser = consumerResponse?.data;
            if (consumerUser) {
              resolvedUser = {
                ...consumerUser,
                role: consumerUser.role || 'consumer',
                permissions:
                  consumerUser.permissions || ['create:loan', 'update:loan'],
                auth_provider: consumerUser.auth_provider || 'consumer',
              };
              resolvedAuthContext = {
                permissions: resolvedUser.permissions,
              };
            }
          } catch (consumerError) {
            if (consumerError?.response?.status !== 401) {
              console.warn('[Login] /b2c/auth/me failed', consumerError);
            }
          }
        }

        // Try B2B /auth/me if no B2C user found
        if (!resolvedUser) {
          const response = await fetchCurrentUser();
          const payload = response?.data?.data ?? response?.data ?? {};
          resolvedUser = payload.user || payload.current_user;
          resolvedAuthContext = payload.authContext;
        }

        if (resolvedUser) {
          cacheUserMetadata(resolvedUser, resolvedAuthContext);
          setUserData(resolvedUser);
          setData(resolvedUser);
          setIsAuthenticated(true);
        } else {
          setIsAuthenticated(false);
        }
      } catch (error) {
        setIsAuthenticated(false);
        if (process.env.NODE_ENV !== 'production') {
          console.warn('[Login] /auth/me failed', error);
        }
      } finally {
        setIsLoadingUser(false);
      }
    };

    loadUser();
  }, []);

  const { data: applicationData } = useGetPersonalDetailsQuery(
    {
      applicationId: data?.pending_application_id,
    },
    {
      skip: !data?.pending_application_id,
    }
  );

  const order = [
    {
      id: 1,
      name: 'personal_details',
    },
    {
      id: 2,
      name: 'loan_details',
    },
    {
      id: 3,
      name: 'employment_and_income_details',
    },
    {
      id: 4,
      name: 'user_asset_details',
    },
    {
      id: 5,
      name: 'user_libality_details',
    },
    {
      id: 6,
      name: 'user_reos_details',
    },
    // {
    //   id: 7,
    //   name: 'user_declarations_details',
    // },
    // {
    //   id: 8,
    //   name: 'user_demographics_details',
    // },
  ];

  //onboarding page conditions
  useEffect(() => {
    if (!isAuthenticated || !data) {
      return;
    }

    const normalizedRole = String(
      data?.role || data?.auth_provider || data?.authProvider || ''
    )
      .trim()
      .toLowerCase();

    if (normalizedRole === 'consumer') {
      dispatch(setUserRole('consumer'));
      setIsOnboarding(false);
      return;
    }

    if (
      !navigationDoneRef.current &&
      hasPermissions(data?.permissions, roles.consumer) &&
      ((data?.pending_application_id && applicationData) ||
        !data?.pending_application_id)
    ) {
      navigationDoneRef.current = true;
      dispatch(setUserRole('consumer'));
      if (data?.pending_application_id) {
        localStorage.setItem('applicationId', data.pending_application_id);
      }
      if (data?.new_user) {
        navigate('/', { replace: true });
        setIsOnboarding(true);
      } else if (data?.user_profile_exists && !data?.pending_application_id) {
        setIsOnboarding(false);
        navigate('/pipeline');
      } else if (data?.user_profile_exists && data?.pending_application_id) {
        setIsOnboarding(false);
        const allFormsFilled = (applicationData?.data?.data?.completionStatus ?? 0) >= 100;
        if (allFormsFilled) {
          navigate('/pipeline');
        } else {
          navigate('/profile');
        }
      } else if (data?.pending_application_id) {
        setIsOnboarding(false);
        navigate('/profile');
      } else {
        navigate('/', { replace: true });
        setIsOnboarding(true);
      }
    } else if (hasPermissions(data?.permissions, roles?.loanOfficer)) {
      dispatch(setUserRole('loanOfficer'));
      setIsOnboarding(false);
      // Honour onboarding state — SSO-provisioned users (and any legacy
      // user with onboarding_completed=false) need to finish onboarding
      // before they can use the workspace.
      if (data?.needs_onboarding || data?.onboarding_completed === false) {
        navigate('/onboard');
      } else {
        navigate('/dashboard');
      }
    } else if (hasPermissions(data?.permissions, roles?.organization)) {
      dispatch(setUserRole('organization'));
      setIsOnboarding(false);
      if (data?.needs_onboarding || data?.onboarding_completed === false) {
        navigate('/onboard');
      } else {
        navigate('/dashboard');
      }
    }
  }, [data, applicationData, isAuthenticated, dispatch, navigate]);

  // NOTE: Removed beforeunload handler that was clearing localStorage on page refresh.
  // This was breaking session persistence. Session cookies handle auth state now,
  // and user data is cached in localStorage for faster UI loading on refresh.
  // If you need to clear state on tab close only (not refresh), use sessionStorage instead.

  const handleLogout = () => {
    performLogout();
  };

  // render verify password screen
  if (userId && code && email) {
    return <VerifyPassword />;
  }
  //render invitation expired screen
  if (location?.pathname === '/verified') {
    return <VerifyPassword userId={userId} code={code} email={email} />;
  }
  // render email verified page
  if (location?.pathname === '/result_page') {
    return <EmailVerified />;
  }

  if (location?.pathname === '/personal-info') {
    return <PersonalInfoCard />;
  }

  if (location.pathname === '/verification-email') {
    return <VerifyEmail />;
  }

  if (location?.pathname === '/session-timeout') {
    return <SessionExpire />;
  }

  // render verify email page
  // if (data?.email_verified === 0) {
  //   return <VerifyEmail />;
  // }

  if (isLoadingUser) {
    return <Loader variant="colorful" FullScreen={true} />;
  }

  const isConsumerUser = String(data?.role || '').trim().toLowerCase() === 'consumer';

  return isAuthenticated ? (
    isConsumerUser ? (
      <B2CDataPreloader
        applicationId={
          data?.pending_application_id || data?.applications?.[0]?.applicationId
        }
      >
        <AppShell
          logout={handleLogout}
          isOnboarding={isOnboarding}
          setIsOnboarding={setIsOnboarding}
          user={userData}
          data={data}
        />
      </B2CDataPreloader>
    ) : (
      <B2BDataPreloader>
        <AppShell
          logout={handleLogout}
          isOnboarding={isOnboarding}
          setIsOnboarding={setIsOnboarding}
          user={userData}
          data={data}
        />
      </B2BDataPreloader>
    )
  ) : (
    <></>
  );
};

export default Login;
